import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { buildMetadata } from "@/lib/metadata";
import { ApiError } from "@/lib/api/api-error";
import { cn } from "@/lib/utils";
import { UploadService } from "@/services/upload-service";
import {
  ProductStockUploadService,
  type StockPreviewRow,
} from "@/services/product-stock-upload-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ReviewStepper } from "./-components/review-stepper";
import { StockStatusBadge } from "./-components/stock-status";
import { ProductService } from "@/services/product-service";
import type { Product } from "@/services/types/products";
import { useEffect } from "react";

type StockBrandOption = StockPreviewRow["brand_options"][number];

type DuplicateRowGroup = {
  key: string;
  sku: string;
  brandName: string | null;
  itemName: string;
  incomingTotal: number;
  rowIds: string[];
  count: number;
};

function buildBrandOptionsFromProduct(
  product?: Product | null,
): StockBrandOption[] {
  return (product?.product_brands ?? [])
    .filter((productBrand) => productBrand.is_active)
    .map((productBrand) => ({
      product_brand_id: productBrand.id,
      brand_id: productBrand.brand_id,
      brand_name: productBrand.brand_name,
      is_default: productBrand.is_default,
    }));
}

function getDefaultBrandId(
  brandOptions: StockBrandOption[],
): number | null {
  const defaultBrand = brandOptions.find((option) => option.is_default);
  if (defaultBrand) {
    return defaultBrand.product_brand_id;
  }
  if (brandOptions.length === 1) {
    return brandOptions[0].product_brand_id;
  }
  return null;
}

function getSelectedBrandName(row: StockPreviewRow): string | null {
  if (!row.selected_product_brand_id) {
    return null;
  }
  return (
    row.brand_options.find(
      (option) => option.product_brand_id === row.selected_product_brand_id,
    )?.brand_name ?? null
  );
}

function buildDuplicateGroups(
  rows: StockPreviewRow[],
  removedIds: Set<string>,
): DuplicateRowGroup[] {
  const groups = new Map<string, DuplicateRowGroup>();

  for (const row of rows) {
    if (removedIds.has(row.id) || !row.sku_id) {
      continue;
    }
    if (row.brand_required && !row.selected_product_brand_id) {
      continue;
    }

    const brandName = getSelectedBrandName(row);
    const key = `${row.sku_id}::${row.selected_product_brand_id ?? "aggregate"}`;
    const existing = groups.get(key);
    if (existing) {
      existing.incomingTotal += row.incoming;
      existing.count += 1;
      existing.rowIds.push(row.id);
      continue;
    }

    groups.set(key, {
      key,
      sku: row.sku_id,
      brandName,
      itemName: row.original_name || row.sku_name,
      incomingTotal: row.incoming,
      rowIds: [row.id],
      count: 1,
    });
  }

  return Array.from(groups.values()).filter((group) => group.count > 1);
}

export const Route = createFileRoute(
  "/_main/products/upload/review/$sessionId",
)({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Review stock update • Products",
      description: "Match SKUs and confirm stock changes.",
      keywords: ["products", "stock", "review"],
    }),
  }),
});

function RouteComponent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const uploadRequestQuery = useQuery({
    queryKey: ["product-stock-upload-request", sessionId],
    queryFn: () => UploadService.getUploadRequest(sessionId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) {
        return false;
      }
      return data.status === "pending" || data.status === "processing"
        ? 3000
        : false;
    },
  });
  const isProcessing =
    uploadRequestQuery.data?.status === "pending" ||
    uploadRequestQuery.data?.status === "processing";

  const { data: allInventoryItems } = useQuery({
    queryKey: ["inventory_products"],
    queryFn: () => ProductService.list({ pageSize: 1000 }),
  });

  const reviewQuery = useQuery({
    queryKey: ["product-stock-upload-review", sessionId],
    queryFn: () => ProductStockUploadService.getReview(sessionId),
    enabled: uploadRequestQuery.data?.status === "completed",
  });

  const [removedUnrecognized, setRemovedUnrecognized] = useState<Set<string>>(
    () => new Set(),
  );
  const [stockItems, setStockItems] = useState<StockPreviewRow[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(
    null,
  );

  const unrecognized = useMemo(() => {
    const items = reviewQuery.data?.unrecognized_items ?? [];
    return items.filter((u) => !removedUnrecognized.has(u.id));
  }, [reviewQuery.data?.unrecognized_items, removedUnrecognized]);
  const duplicateGroups = useMemo(
    () => buildDuplicateGroups(stockItems, removedUnrecognized),
    [stockItems, removedUnrecognized],
  );
  const duplicateRowIds = useMemo(
    () => new Set(duplicateGroups.flatMap((group) => group.rowIds)),
    [duplicateGroups],
  );

  const hasMissingBrandSelection = stockItems.some(
    (row) =>
      row.brand_required &&
      !removedUnrecognized.has(row.id) &&
      !row.selected_product_brand_id,
  );

  useEffect(() => {
    setStockItems(reviewQuery.data?.stock_items ?? []);
  }, [reviewQuery.data?.stock_items]);

  useEffect(() => {
    const selectedWarehouse = reviewQuery.data?.selected_warehouse_id;
    if (selectedWarehouse) {
      setSelectedWarehouseId(selectedWarehouse);
      return;
    }
    if ((reviewQuery.data?.available_warehouses.length ?? 0) === 1) {
      setSelectedWarehouseId(reviewQuery.data?.available_warehouses[0]?.id ?? null);
    }
  }, [
    reviewQuery.data?.selected_warehouse_id,
    reviewQuery.data?.available_warehouses,
  ]);

  const handleAction = async () => {
    try {
      await confirmMutation.mutateAsync();
      await applyMutation.mutateAsync();
      await router.invalidate();
      navigate({ to: "/products" });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const applyMutation = useMutation({
    mutationFn: () => ProductStockUploadService.apply(sessionId),
    onSuccess: async (data) => {
      toast.success(`Updated ${data.updated_count} product stock levels.`);
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_products"] });
      await queryClient.invalidateQueries({
        queryKey: ["product-stock-upload-preview", sessionId],
      });
    },
    onError: () => {
      toast.error("Failed to apply stock updates.");
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      const skuOverrides = stockItems.reduce<Record<string, string>>((acc, row) => {
        if (row.sku_id) {
          acc[row.id] = row.sku_id;
        }
        return acc;
      }, {});
      const brandOverrides = stockItems.reduce<Record<string, number>>(
        (acc, row) => {
          if (row.brand_required && row.selected_product_brand_id) {
            acc[row.id] = row.selected_product_brand_id;
          }
          return acc;
        },
        {},
      );
      return ProductStockUploadService.confirm(sessionId, {
        warehouse_id: selectedWarehouseId ?? undefined,
        sku_overrides: skuOverrides,
        brand_overrides: brandOverrides,
        removed_unrecognized_ids: Array.from(removedUnrecognized),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["product-stock-upload-review", sessionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["product-stock-upload-preview", sessionId],
      });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        const body = error.body as
          | { detail?: string; duplicate_items?: unknown[] }
          | undefined;
        if (body?.detail) {
          toast.error(body.detail);
          return;
        }
      }
      toast.error("Could not confirm. Try again.");
    },
  });

  const getSelectedCandidate = (row: StockPreviewRow) => {
    const selectedSku = row.sku_id;
    return (
      row.sku_matches.find((candidate) => candidate.sku === selectedSku) ?? null
    );
  };

  if (uploadRequestQuery.isPending || isProcessing) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-8">
        <div className="space-y-3 text-center">
          <Loader2 className="mx-auto size-10 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            Processing your stock document...
          </p>
        </div>
      </div>
    );
  }

  if (
    uploadRequestQuery.isError ||
    uploadRequestQuery.data?.status === "failed" ||
    reviewQuery.isError ||
    !reviewQuery.data
  ) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-destructive">
          {uploadRequestQuery.data?.error_message || "Could not load review data."}
        </p>
        <Button asChild variant="outline">
          <Link to="/products/upload">Back to upload</Link>
        </Button>
      </div>
    );
  }

  const data = reviewQuery.data;
  const lineLabel = `${data.line_items} line items`;
  const warehouseSelectionRequired = data.available_warehouses.length > 1;

  return (
    <div className="space-y-8 p-4 pb-28 md:p-8">
      <div className="space-y-6">
        <Link
          to="/products/upload"
          className="inline-flex text-muted-foreground text-sm hover:text-foreground"
        >
          ← Upload a different file
        </Link>
        <div className="space-y-2">
          <h1 className="font-semibold text-2xl tracking-tight">
            Review &amp; Confirm Stock Update
          </h1>
          <p className="text-muted-foreground text-sm">
            Match SKUs and confirm changes · {data.filename} · {lineLabel}
          </p>
        </div>
        <ReviewStepper activeIndex={1} />
      </div>

      {unrecognized.length > 0 ? (
        <section
          className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 md:p-6"
          aria-label="Unrecognized items"
        >
          <h2 className="font-semibold text-amber-950">
            Unrecognized Items ({unrecognized.length})
          </h2>
          <p className="mt-1 text-amber-900/80 text-sm">
            These items couldn&apos;t be matched. Resolve or remove before
            confirming.
          </p>
          <ul className="mt-4 space-y-3">
            {unrecognized.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background px-4 py-3"
              >
                <span className="font-medium">{item.name}</span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    hidden={true}
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() =>
                      toast.message("Resolve", {
                        description:
                          "Mapping UI can be wired when ERP rules are ready.",
                      })
                    }
                  >
                    Resolve
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Remove ${item.name}`}
                    onClick={() =>
                      setRemovedUnrecognized((prev) =>
                        new Set(prev).add(item.id),
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {duplicateGroups.length > 0 ? (
        <section
          className="rounded-xl border border-red-200/80 bg-red-50/80 p-4 md:p-6"
          aria-label="Duplicate items"
        >
          <h2 className="font-semibold text-red-950">
            Duplicate Product Rows ({duplicateGroups.length})
          </h2>
          <p className="mt-1 text-red-900/80 text-sm">
            Duplicate rows would silently sum stock. Consolidate or remove them
            before confirming this upload.
          </p>
          <ul className="mt-4 space-y-3">
            {duplicateGroups.map((group) => (
              <li
                key={group.key}
                className="rounded-lg border bg-background px-4 py-3 text-sm"
              >
                <span className="font-medium">
                  {group.itemName} ({group.sku})
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  appears {group.count} times
                  {group.brandName ? ` for brand ${group.brandName}` : ""} with
                  incoming total +{group.incomingTotal}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-xl border bg-card">
        <div className="flex flex-col gap-4 border-b px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-lg">Stock Items</h2>
            <Badge className="rounded-md border-0 bg-brand-yellowGreen font-medium text-brand-darkGreen hover:bg-brand-yellowGreen">
              {data.stats.total_items} items
            </Badge>
            <Badge
              variant="outline"
              className="rounded-md border-red-200 bg-red-50 font-medium text-red-700"
            >
              {data.stats.low_stock_count} low stock
            </Badge>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            {data.available_warehouses.length > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Warehouse</span>
                <Select
                  value={selectedWarehouseId ? String(selectedWarehouseId) : undefined}
                  onValueChange={(value) => setSelectedWarehouseId(Number(value))}
                >
                  <SelectTrigger className="h-9 w-64 rounded-md">
                    {selectedWarehouseId
                      ? data.available_warehouses.find(
                          (warehouse) => warehouse.id === selectedWarehouseId,
                        )?.name
                      : "Select warehouse"}
                  </SelectTrigger>
                  <SelectContent className="w-72">
                    {data.available_warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={String(warehouse.id)}>
                        {warehouse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit rounded-lg"
              onClick={() =>
                toast.message("Add item", {
                  description:
                    "Manual line items can be added when the API supports it.",
                })
              }
            >
              <Plus className="mr-1 size-4" />
              Add item
            </Button>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="font-semibold" style={{"width":"350px"}}>SKU Name</TableHead>
              <TableHead className="font-semibold">SKU ID</TableHead>
              <TableHead className="font-semibold">Brand</TableHead>
              <TableHead className="text-center font-semibold">
                Old Stock
              </TableHead>
              <TableHead className="text-center font-semibold">
                Incoming
              </TableHead>
              <TableHead className="text-center font-semibold">
                New Stock
              </TableHead>
              <TableHead className="font-semibold">UOM</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="w-[72px] text-right font-semibold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stockItems.map((thisRow) => {
              if(thisRow.original_name == "") {
                thisRow.original_name = thisRow.sku_name;
              }
              return <TableRow
                key={thisRow.id}
                className={cn(
                  duplicateRowIds.has(thisRow.id) ? "bg-red-50/60" : undefined,
                )}
              >
                <TableCell className="font-medium" width={"40%"}>{thisRow.original_name}</TableCell>
                <TableCell width={"20%"}>
                  <Select
                    value={thisRow.sku_id}
                    onValueChange={(v) => {
                      const selectedItem = allInventoryItems?.results.find(
                        (item) => item.sku === v,
                      );
                      const nextName = selectedItem?.name ?? thisRow.sku_name;
                      const brandOptions =
                        buildBrandOptionsFromProduct(selectedItem);
                      const selectedProductBrandId =
                        getDefaultBrandId(brandOptions);

                      setStockItems((prev) =>
                        prev.map((item) =>
                          item.id === thisRow.id
                            ? {
                                ...item,
                                sku_id: v,
                                sku_name: nextName,
                                brand_required: brandOptions.length > 0,
                                brand_options: brandOptions,
                                selected_product_brand_id:
                                  selectedProductBrandId,
                              }
                            : item,
                        ),
                      );
                    }}
                  >
                    <SelectTrigger className="h-9 w-[min(140px,100%)] rounded-md">
                      <SkuOptionLabel
                        sku={thisRow.sku_id}
                        name={thisRow.sku_name}
                        similarityScore={
                          getSelectedCandidate(thisRow)?.similarity_score ?? null
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="w-72">
                      {thisRow.sku_id_options.map((opt: any) => {
                        const candidate =
                          thisRow.sku_matches.find((entry: any) => entry.sku === opt) ?? null;
                        return (
                        <SelectItem key={opt} value={opt}>
                          <div className="flex w-full min-w-0 flex-col items-start gap-1 py-0.5">
                            <div className="flex w-full items-center justify-between gap-2">
                              <span className="truncate font-medium text-xs">
                                {candidate?.name || thisRow.sku_name || "Unnamed item"}
                              </span>
                              {candidate?.similarity_score !== null &&
                              candidate?.similarity_score !== undefined ? (
                                <Badge
                                  variant="secondary"
                                  className="rounded-full px-2 py-0 text-[10px]"
                                >
                                  {Math.round(candidate.similarity_score)}%
                                </Badge>
                              ) : null}
                            </div>
                            <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                              <span>{opt}</span>
                            </div>
                          </div>
                        </SelectItem>
                        );
                      })}
                      {allInventoryItems?.results.map((opt) => {
                        const candidate =
                          thisRow.sku_matches.find((entry: any) => entry.sku === opt.sku) ?? null;
                        return (
                        <SelectItem key={opt.sku} value={opt.sku}>
                          <div className="flex w-full min-w-0 flex-col items-start gap-1 py-0.5">
                            <div className="flex w-full items-center justify-between gap-2">
                              <span className="truncate font-medium text-xs">
                                {opt.name}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                              <span>{opt.sku}</span>
                            </div>
                          </div>
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell width={"20%"}>
                  {thisRow.brand_required ? (
                    <Select
                      value={
                        thisRow.selected_product_brand_id
                          ? String(thisRow.selected_product_brand_id)
                          : undefined
                      }
                      onValueChange={(value) =>
                        setStockItems((prev) =>
                          prev.map((item) =>
                            item.id === thisRow.id
                              ? {
                                  ...item,
                                  selected_product_brand_id: Number(value),
                                }
                              : item,
                          ),
                        )
                      }
                    >
                      <SelectTrigger className="h-9 w-[min(180px,100%)] rounded-md">
                        {thisRow.selected_product_brand_id
                          ? thisRow.brand_options.find(
                              (option) =>
                                option.product_brand_id ===
                                thisRow.selected_product_brand_id,
                            )?.brand_name
                          : "Select brand"}
                      </SelectTrigger>
                      <SelectContent className="w-64">
                        {thisRow.brand_options.map((option) => (
                          <SelectItem
                            key={option.product_brand_id}
                            value={String(option.product_brand_id)}
                          >
                            <div className="flex w-full min-w-0 items-center justify-between gap-2">
                              <span className="truncate font-medium text-xs">
                                {option.brand_name}
                              </span>
                              {option.is_default ? (
                                <Badge
                                  variant="secondary"
                                  className="rounded-full px-2 py-0 text-[10px]"
                                >
                                  Default
                                </Badge>
                              ) : null}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className="font-normal text-xs">
                      Warehouse only
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-center" width={"5%"}>
                  <span
                    className={cn(
                      "inline-flex min-w-[3rem] justify-center rounded-md border bg-muted/40 px-2 py-1 tabular-nums",
                    )}
                  >
                    {thisRow.old_stock}
                  </span>
                </TableCell>
                <TableCell className="text-center" width={"5%"}>
                  <span className="inline-flex min-w-[3rem] justify-center rounded-md border bg-muted/40 px-2 py-1 tabular-nums">
                    {thisRow.incoming}
                  </span>
                </TableCell>
                <TableCell className="text-center" width={"5%"}>
                  <span className="inline-flex min-w-[3rem] justify-center rounded-md border bg-muted/40 px-2 py-1 tabular-nums">
                    {thisRow.new_stock}
                  </span>
                </TableCell>
                <TableCell width={"5%"}>
                  <span className="rounded-md border bg-muted/50 px-2 py-1 text-xs">
                    {thisRow.uom}
                  </span>
                </TableCell>
                <TableCell width={"5%"}>
                  <StockStatusBadge status={thisRow.status} />
                </TableCell>
                <TableCell className="text-right" width={"5%"}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground"
                    aria-label={`Edit ${thisRow.sku_name}`}
                    onClick={() =>
                      toast.message("Edit row", {
                        description:
                          "Inline editing can connect to PATCH when available.",
                      })
                    }
                  >
                    <Pencil className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            })}
          </TableBody>
        </Table>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-10 flex justify-end gap-3 border-t bg-background/95 px-4 py-4 backdrop-blur md:px-8">
        <Button variant="outline" className="rounded-lg" asChild>
          <Link to="/products">Discard &amp; Cancel</Link>
        </Button>
        <Button
          className="rounded-lg bg-foreground text-background hover:bg-foreground/90"
          disabled={
            confirmMutation.isPending ||
            applyMutation.isPending ||
            (warehouseSelectionRequired && !selectedWarehouseId) ||
            hasMissingBrandSelection ||
            duplicateGroups.length > 0
          }
          onClick={() => handleAction()}
        >
          {confirmMutation.isPending || applyMutation.isPending ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Confirming…
            </>
          ) : (
            "Confirm & Sync"
          )}
        </Button>
      </div>
    </div>
  );
}

function SkuOptionLabel({
  sku,
  name,
  similarityScore,
}: {
  sku: string;
  name: string;
  similarityScore: number | null;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="min-w-0">
        <div className="truncate font-medium text-xs">{sku}</div>
        <div className="truncate text-[10px] text-muted-foreground">{name}</div>
      </div>
      {similarityScore !== null && similarityScore !== undefined ? (
        <Badge
          variant="secondary"
          className="ml-auto rounded-full px-2 py-0 text-[10px]"
        >
          {Math.round(similarityScore)}%
        </Badge>
      ) : null}
    </div>
  );
}
